import React, { useState } from 'react';
import { Dices, Hand, Play, Shuffle } from 'lucide-react';
import { describeRule, drawLetters, LETTER_POS_LABELS, LetterPos, LetterRule, letterAllowedAt, randomLetter, RULE_PRESETS } from '../../../utils/initials';
import { GameAction, GameState, nextStarter, otherSide, sideName } from '../../../utils/initialsGame';
import { Btn, LetterGrid, LetterPair, Panel, PanelTitle } from './parts';

const drawRule = (): LetterRule => RULE_PRESETS[Math.floor(Math.random() * RULE_PRESETS.length)].rule;

interface Props {
  state: GameState;
  dispatch: (action: GameAction) => void;
}

/**
 * Before a round. By default the platform draws the letters; the sides can
 * still pick them by hand — the opening side the name's letter, the other
 * side the surname's.
 */
const InitialsPick: React.FC<Props> = ({ state, dispatch }) => {
  const random = state.settings.rule === 'random';
  const [rule, setRule] = useState<LetterRule>(() => (state.settings.rule === 'random' ? drawRule() : state.settings.rule));
  const [manual, setManual] = useState(state.settings.letterSource === 'manual');
  const [letters, setLetters] = useState(() =>
    state.settings.letterSource === 'manual' ? { name: '', surname: '' } : drawLetters(rule, state.rounds)
  );

  const starter = nextStarter(state);
  const namePicker = starter;
  const surnamePicker = otherSide(starter);

  const redrawLetters = (forRule: LetterRule = rule) => setLetters(drawLetters(forRule, state.rounds));

  const redrawRule = () => {
    const next = drawRule();
    setRule(next);
    if (!manual) {
      redrawLetters(next);
      return;
    }
    // A letter picked for another position may not fit the new one (Ğ can't open a word).
    setLetters(l => ({
      name: l.name && letterAllowedAt(l.name, next.name) ? l.name : '',
      surname: l.surname && letterAllowedAt(l.surname, next.surname) ? l.surname : '',
    }));
  };

  const switchToManual = () => {
    setManual(true);
    setLetters({ name: '', surname: '' });
  };

  const switchToAuto = () => {
    setManual(false);
    redrawLetters();
  };

  const picker = (label: string, pos: LetterPos, by: string, part: 'name' | 'surname') => (
    <Panel>
      <PanelTitle
        aside={
          <Btn tone="ghost" className="px-3 py-1.5" onClick={() => setLetters(l => ({ ...l, [part]: randomLetter(pos) }))}>
            <Dices className="h-4 w-4" />
            Təsadüfi
          </Btn>
        }
      >
        {label} <span className="font-normal text-white/50">— {by} seçir</span>
      </PanelTitle>
      <div className="p-4 sm:p-5">
        <LetterGrid value={letters[part]} pos={pos} onChange={letter => setLetters(l => ({ ...l, [part]: letter }))} />
      </div>
    </Panel>
  );

  const startButton = (
    <Btn
      className="px-6 py-3 text-base"
      disabled={!letters.name || !letters.surname}
      onClick={() =>
        dispatch({
          type: 'startRound',
          rule,
          letters,
          pickedBy: manual ? { name: namePicker, surname: surnamePicker } : null,
          now: Date.now(),
        })
      }
    >
      <Play className="h-4 w-4" />
      Raundu başlat
    </Btn>
  );

  return (
    <div className="flex flex-col gap-5">
      <Panel className="flex flex-col items-center gap-2 px-4 py-5 text-center">
        <span className="text-sm font-semibold uppercase tracking-wider text-white/40">Raund {state.rounds.length + 1}</span>
        <LetterPair name={letters.name || '?'} surname={letters.surname || '?'} className="text-6xl sm:text-7xl" />
        <p className="text-white/70">{describeRule(rule)}</p>
        <p className="text-sm text-white/40">İlk gedişi {sideName(state, starter)} edir.</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {!manual && (
            <Btn tone="ghost" className="px-3 py-1.5" onClick={() => redrawLetters()}>
              <Shuffle className="h-4 w-4" />
              Başqa hərflər
            </Btn>
          )}
          {random && (
            <Btn tone="ghost" className="px-3 py-1.5" onClick={redrawRule}>
              <Dices className="h-4 w-4" />
              Başqa qayda
            </Btn>
          )}
          {manual ? (
            <Btn tone="ghost" className="px-3 py-1.5" onClick={switchToAuto}>
              <Shuffle className="h-4 w-4" />
              Hərfləri platforma seçsin
            </Btn>
          ) : (
            <Btn tone="ghost" className="px-3 py-1.5" onClick={switchToManual}>
              <Hand className="h-4 w-4" />
              Hərfləri özümüz seçək
            </Btn>
          )}
        </div>
        {!manual && <div className="mt-3">{startButton}</div>}
      </Panel>

      {manual && (
        <>
          <div className="grid gap-5 lg:grid-cols-2">
            {picker(`Adın ${LETTER_POS_LABELS[rule.name]} hərfi`, rule.name, sideName(state, namePicker), 'name')}
            {picker(`Soyadın ${LETTER_POS_LABELS[rule.surname]} hərfi`, rule.surname, sideName(state, surnamePicker), 'surname')}
          </div>
          <div className="self-end">{startButton}</div>
        </>
      )}
    </div>
  );
};

export default InitialsPick;
